import { z } from 'zod';

/**
 * What a Questionnaire step stores in `WorkflowStep.Settings`.
 *
 * A POINTER, not a copy. The step records which questionnaire to ask; the questions themselves stay
 * in the questionnaire, so fixing a typo updates every workflow that uses it rather than every
 * workflow keeping its own diverging copy.
 *
 * ONE questionnaire per step — a workflow that collects two sets of answers adds two steps, and the
 * workflow's own step order is the order the applicant meets them. A second ordering hidden inside
 * one step's settings would be invisible from the builder's step list.
 *
 * Mirrors `templateStepSettings.ts` deliberately, including the merge-preserving write: the
 * `Settings` blob is shared with the generic settings tab and the advanced-rules grouping, so
 * anything that writes it must not clobber keys it does not own.
 */
export const questionnaireStepSettingsSchema = z.object({
  questionnaireId: z.string().nullable().default(null),
  /**
   * Cached for display before the list loads. NOT authoritative — the questionnaire list is, so a
   * renamed questionnaire shows its new name rather than the name it had when it was attached.
   */
  questionnaireName: z.string().nullable().default(null),
});

export type QuestionnaireStepSettings = z.infer<typeof questionnaireStepSettingsSchema>;

const EMPTY: QuestionnaireStepSettings = { questionnaireId: null, questionnaireName: null };

/**
 * Reads the questionnaire settings out of a step's raw settings JSON.
 *
 * Tolerant by design: the blob carries every other step type's settings too, and a step may
 * predate this feature. Anything unparseable reads as "nothing attached", which the panel then
 * prompts to fix — rather than throwing inside the workflow editor.
 */
export function parseQuestionnaireStepSettings(raw: string | null): QuestionnaireStepSettings {
  if (!raw) return EMPTY;

  try {
    const parsed = questionnaireStepSettingsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : EMPTY;
  } catch {
    return EMPTY;
  }
}

/**
 * Merges questionnaire settings back into a step's settings JSON, preserving every key this
 * feature does not own.
 */
export function writeQuestionnaireStepSettings(
  raw: string | null,
  next: Partial<QuestionnaireStepSettings>,
): string {
  let base: Record<string, unknown> = {};
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') base = parsed as Record<string, unknown>;
    } catch {
      /* unparseable — start clean rather than lose the new link */
    }
  }

  return JSON.stringify({ ...base, ...next });
}
